import {
  DynamicDrawUsage,
  type Group,
  InstancedMesh,
  type Intersection,
  Material,
  Matrix4,
  Mesh,
  MeshStandardMaterial,
  NormalBlending,
  Object3D,
  type Raycaster,
} from "three";
import { markProductSceneChanged } from "./sceneActivity";
import {
  compileHookAllowsHardwareInstancing,
  hardwareMaterialKey,
} from "./supplyPartMaterials";

interface HardwareInstance {
  mesh: Mesh;
  material: Material;
  /** Resolved source materials are borrowed/read-only, unlike mesh state. */
  materialKey: string;
  renderKey: string;
  batch: HardwareBatch;
  partId: string;
  matrix: Matrix4;
  uploadedMatrix: Matrix4 | null;
  raycast: Mesh["raycast"];
  /**
   * The proxy installed while this entry is drawn by its batch, built once.
   * `updateVisual` runs for every entry on every flush — i.e. on every frame
   * of a drag — so minting a fresh closure there churned one allocation per
   * hardware part per frame for a function that never varies.
   */
  proxyRaycast: Mesh["raycast"] | null;
  instanceId: number | undefined;
  visible: boolean;
}

interface HardwareBatch {
  key: string;
  entries: Set<HardwareInstance>;
  visual: InstancedMesh | null;
  proxyMaterial: Material | null;
  capacity: number;
}

export interface HardwareRegistration {
  partId: string;
  dynamic?: boolean;
}

/**
 * Deliberately conservative: imported opaque PBR meshes without per-object
 * shading can be instanced; everything else keeps its ordinary renderer.
 */
export function canInstanceHardware(mesh: Mesh): boolean {
  const material = mesh.material;
  return (
    mesh.constructor === Mesh &&
    material instanceof MeshStandardMaterial &&
    material.constructor === MeshStandardMaterial &&
    material.type === "MeshStandardMaterial" &&
    material.visible &&
    !material.transparent &&
    material.opacity === 1 &&
    material.alphaTest === 0 &&
    !material.alphaHash &&
    material.blending === NormalBlending &&
    material.depthTest &&
    material.depthWrite &&
    material.colorWrite &&
    !material.stencilWrite &&
    !material.wireframe &&
    !material.clippingPlanes?.length &&
    Object.keys(material.userData).length === 0 &&
    compileHookAllowsHardwareInstancing(material.onBeforeCompile) &&
    material.customProgramCacheKey ===
      Material.prototype.customProgramCacheKey &&
    !mesh.customDepthMaterial &&
    !mesh.customDistanceMaterial &&
    !mesh.morphTargetInfluences?.length &&
    Object.keys(mesh.geometry.morphAttributes).length === 0 &&
    mesh.animations.length === 0 &&
    mesh.onBeforeRender === Object3D.prototype.onBeforeRender &&
    mesh.onAfterRender === Object3D.prototype.onAfterRender &&
    mesh.onBeforeShadow === Object3D.prototype.onBeforeShadow &&
    mesh.onAfterShadow === Object3D.prototype.onAfterShadow &&
    mesh.raycast === Mesh.prototype.raycast
  );
}

function renderStateKey(mesh: Mesh): string {
  return [
    mesh.geometry.uuid,
    mesh.castShadow,
    mesh.receiveShadow,
    mesh.renderOrder,
    mesh.layers.mask,
    mesh.frustumCulled,
  ].join("|");
}

const noRaycast = () => {};

function proxyRaycast(
  entry: HardwareInstance,
  raycaster: Raycaster,
  intersections: Intersection[],
): void {
  const start = intersections.length;
  entry.raycast.call(entry.mesh, raycaster, intersections);
  for (let index = start; index < intersections.length; index++) {
    const hit = intersections[index];
    if (hit) hit.instanceId = entry.instanceId;
  }
}

function supportsInstanceMatrix(matrix: Matrix4): boolean {
  if (matrix.determinant() <= 0) return false;
  const e = matrix.elements;
  const x = Math.hypot(e[0], e[1], e[2]);
  const y = Math.hypot(e[4], e[5], e[6]);
  const z = Math.hypot(e[8], e[9], e[10]);
  if (!Number.isFinite(x + y + z) || x * y * z === 0) return false;
  // InstancedMesh does not support reflected transforms or sheared normals.
  return (
    Math.abs(e[0] * e[4] + e[1] * e[5] + e[2] * e[6]) < 1e-6 * x * y &&
    Math.abs(e[0] * e[8] + e[1] * e[9] + e[2] * e[10]) < 1e-6 * x * z &&
    Math.abs(e[4] * e[8] + e[5] * e[9] + e[6] * e[10]) < 1e-6 * y * z
  );
}

/**
 * One manager per animation group. Visual batches share that group's transform;
 * logical meshes stay below their part's event handlers with their geometry
 * intact for raycasting, glow and bounds. A non-visible material prevents
 * duplicate draws without making the logical object disappear from those APIs.
 */
export class HardwareInstanceManager {
  private root: Group | null = null;
  private dirty = false;
  private publishingChange = false;
  private readonly entries = new Set<HardwareInstance>();
  private readonly batches = new Map<string, HardwareBatch>();

  constructor(
    readonly animationGroup: string,
    private readonly invalidate: () => void = noRaycast,
  ) {}

  attach(root: Group): void {
    this.root = root;
    this.markDirty();
  }

  /**
   * Flags the batches for a re-read on the next rendered frame *without* asking
   * for one.
   *
   * Use this where the caller cannot say whether anything actually moved — a
   * plain React render, say. Whatever did move (an R3F prop write, an animation
   * tick, a drag) schedules its own frame, so requesting one from here as well
   * would wake a `frameloop="demand"` canvas on every render for nothing.
   */
  markStale(): void {
    if (this.publishingChange) return;
    this.dirty = true;
  }

  /** Flags the batches and asks for the frame that will re-read them. */
  markDirty(): void {
    if (this.publishingChange) return;
    this.dirty = true;
    this.invalidate();
  }

  register(mesh: Mesh, options: HardwareRegistration): () => void {
    if (options.dynamic || !canInstanceHardware(mesh)) return noRaycast;
    const material = mesh.material as Material;
    const materialKey = hardwareMaterialKey(material);
    const renderKey = renderStateKey(mesh);
    const batch = this.getBatch(`${renderKey}|${materialKey}`);
    const entry: HardwareInstance = {
      mesh,
      material,
      materialKey,
      renderKey,
      batch,
      partId: options.partId,
      matrix: new Matrix4(),
      uploadedMatrix: null,
      raycast: mesh.raycast,
      proxyRaycast: null,
      instanceId: undefined,
      visible: false,
    };
    this.entries.add(entry);
    batch.entries.add(entry);
    this.markDirty();
    return () => {
      // An entry may have moved batches or outlived dispose/re-attach. In the
      // latter case even restoring it could overwrite a new registration's proxy.
      if (!this.entries.delete(entry)) return;
      const batch = entry.batch;
      this.restore(entry);
      batch.entries.delete(entry);
      // Remove stale visuals immediately, before the renderer releases materials.
      const changed = this.removeVisual(batch);
      if (batch.entries.size === 0 && this.batches.get(batch.key) === batch) {
        this.batches.delete(batch.key);
      }
      this.markDirty();
      if (changed) this.publishChange();
    };
  }

  /** Constant-time when idle; traverses registered paths only after scene edits. */
  flush(): boolean {
    if (!this.dirty || !this.root) return false;
    this.dirty = false;
    let changed = this.reconcileBatches();
    const updated = new Set<Object3D>();
    for (const batch of this.batches.values()) {
      const active: HardwareInstance[] = [];
      for (const entry of batch.entries) {
        entry.visible = this.readTransform(entry, updated);
        if (entry.visible) active.push(entry);
      }
      if (active.length < 2) {
        changed = this.removeVisual(batch) || changed;
        continue;
      }
      changed = this.updateVisual(batch, active) || changed;
    }
    if (changed) this.publishChange();
    return changed;
  }

  private getBatch(key: string): HardwareBatch {
    let batch = this.batches.get(key);
    if (!batch) {
      batch = {
        key,
        entries: new Set(),
        visual: null,
        proxyMaterial: null,
        capacity: 0,
      };
      this.batches.set(key, batch);
    }
    return batch;
  }

  private reconcileBatches(): boolean {
    let changed = false;
    // Complete all moves before drawing; iterating registrations avoids visiting
    // moved entries twice as batch sets change. Never key/test the hidden proxy
    // material or proxy raycast as if they were the source mesh's render state.
    for (const entry of this.entries) {
      const renderKey = renderStateKey(entry.mesh);
      if (renderKey === entry.renderKey) continue;
      const previous = entry.batch;
      this.restore(entry);
      previous.entries.delete(entry);
      if (previous.entries.size === 0) {
        changed = this.removeVisual(previous) || changed;
        this.batches.delete(previous.key);
      }
      entry.renderKey = renderKey;
      entry.batch = this.getBatch(`${renderKey}|${entry.materialKey}`);
      entry.batch.entries.add(entry);
    }
    return changed;
  }

  private readTransform(
    entry: HardwareInstance,
    updated: Set<Object3D>,
  ): boolean {
    const matrix = entry.matrix.identity();
    let current: Object3D | null = entry.mesh;
    while (current && current !== this.root) {
      if (!current.visible || current.animations.length > 0) return false;
      if (current !== entry.mesh && current.renderOrder !== 0) return false;
      if (current.matrixAutoUpdate && !updated.has(current)) {
        current.updateMatrix();
        updated.add(current);
      }
      matrix.premultiply(current.matrix);
      current = current.parent;
    }
    return current === this.root && supportsInstanceMatrix(matrix);
  }

  private updateVisual(
    batch: HardwareBatch,
    active: HardwareInstance[],
  ): boolean {
    const first = active[0];
    if (!first || !this.root) return false;
    let changed = false;
    if (!batch.visual || batch.capacity < active.length) {
      this.removeVisual(batch);
      batch.capacity = 2 ** Math.ceil(Math.log2(active.length));
      const visual = new InstancedMesh(
        first.mesh.geometry,
        first.material.clone(),
        batch.capacity,
      );
      visual.name = `hardware-batch:${this.animationGroup}`;
      visual.userData.isHardwareBatch = true;
      visual.instanceMatrix.setUsage(DynamicDrawUsage);
      visual.castShadow = first.mesh.castShadow;
      visual.receiveShadow = first.mesh.receiveShadow;
      visual.renderOrder = first.mesh.renderOrder;
      visual.layers.mask = first.mesh.layers.mask;
      visual.frustumCulled = first.mesh.frustumCulled;
      // R3F raycasts descendants of event-bound *part* groups, not this sibling.
      // Keep picking there, otherwise moving a draw out of the part breaks drag.
      visual.raycast = noRaycast;
      visual.matrixAutoUpdate = false;
      batch.visual = visual;
      batch.proxyMaterial = first.material.clone();
      batch.proxyMaterial.visible = false;
      this.root.add(visual);
      changed = true;
    }
    const visual = batch.visual;
    const proxyMaterial = batch.proxyMaterial;
    if (!proxyMaterial) return false;
    if (visual.count !== active.length) changed = true;
    visual.count = active.length;
    const partIds: string[] = [];
    for (let index = 0; index < active.length; index++) {
      const entry = active[index];
      if (!entry) continue;
      if (
        entry.instanceId !== index ||
        !entry.uploadedMatrix?.equals(entry.matrix)
      ) {
        visual.setMatrixAt(index, entry.matrix);
        entry.uploadedMatrix ??= new Matrix4();
        entry.uploadedMatrix.copy(entry.matrix);
        changed = true;
      }
      entry.instanceId = index;
      entry.mesh.material = proxyMaterial;
      entry.proxyRaycast ??= (raycaster, intersections) =>
        proxyRaycast(entry, raycaster, intersections);
      entry.mesh.raycast = entry.proxyRaycast;
      partIds.push(entry.partId);
    }
    for (const entry of batch.entries) {
      if (!entry.visible) this.restore(entry);
    }
    visual.userData.hardwarePartIds = partIds;
    visual.userData.hardwareAnimationGroup = this.animationGroup;
    if (changed) {
      visual.instanceMatrix.needsUpdate = true;
      visual.computeBoundingBox();
      visual.computeBoundingSphere();
    }
    return changed;
  }

  private restore(entry: HardwareInstance): void {
    entry.mesh.material = entry.material;
    entry.mesh.raycast = entry.raycast;
    entry.instanceId = undefined;
    entry.uploadedMatrix = null;
  }

  private removeVisual(batch: HardwareBatch): boolean {
    const changed = batch.visual !== null;
    for (const entry of batch.entries) this.restore(entry);
    if (batch.visual) {
      batch.visual.removeFromParent();
      // InstancedMesh.dispose releases the instance buffer, not cached geometry.
      batch.visual.dispose();
      (batch.visual.material as Material).dispose();
      batch.visual = null;
    }
    batch.proxyMaterial?.dispose();
    batch.proxyMaterial = null;
    batch.capacity = 0;
    return changed;
  }

  private publishChange(): void {
    if (!this.root) return;
    // Notify bounds/postprocessing only after the replacement draw is ready,
    // without treating our own notification as another transform change.
    this.publishingChange = true;
    try {
      markProductSceneChanged(this.root);
    } finally {
      this.publishingChange = false;
    }
  }

  dispose(): void {
    let changed = false;
    for (const batch of this.batches.values()) {
      changed = this.removeVisual(batch) || changed;
    }
    this.entries.clear();
    this.batches.clear();
    if (changed) this.publishChange();
    this.root = null;
    this.dirty = false;
  }
}
